/**
 * InvoicesPage — Issue #329.
 *
 * 真实发票管理页面：列表 + 新建草稿 + 提交 + 开票 + 驳回 + 作废。
 * 通过 @jinzhuan/api-client 走真实后端，禁止前端伪状态。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Decimal from 'decimal.js';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useInvoices,
  useInvoiceActions,
  type CreateInvoiceDto,
  type InvoiceSummaryDto,
} from '@/hooks/useFinanceExtended';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  submitted: '已提交',
  issued: '已开票',
  rejected: '已驳回',
  voided: '已作废',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-800',
  issued: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  voided: 'bg-gray-200 text-gray-500',
};

const INVOICE_TYPE_LABELS: Record<string, string> = {
  personal: '个人',
  company: '公司',
  vat_special: '增值税专用',
};

const INVOICE_METHOD_LABELS: Record<string, string> = {
  electronic: '电子',
  paper: '纸质',
};

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateInvoiceDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { create, loading, error } = useInvoiceActions();
  const [form, setForm] = useState<CreateInvoiceDto>({
    customerId: '',
    title: '',
    invoiceType: 'personal',
    invoiceMethod: 'electronic',
    amount: '',
    currency: 'CNY',
  });

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = await create(form);
    if (result) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">新建发票草稿</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">客户 ID</label>
            <input
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.customerId}
              onChange={(e) => setForm({ ...form, customerId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">发票抬头</label>
            <input
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">发票类型</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.invoiceType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    invoiceType: e.target.value as CreateInvoiceDto['invoiceType'],
                  })
                }
              >
                <option value="personal">个人</option>
                <option value="company">公司</option>
                <option value="vat_special">增值税专用</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">出票方式</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.invoiceMethod}
                onChange={(e) =>
                  setForm({
                    ...form,
                    invoiceMethod: e.target.value as CreateInvoiceDto['invoiceMethod'],
                  })
                }
              >
                <option value="electronic">电子</option>
                <option value="paper">纸质</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">金额（元）</label>
            <input
              required
              type="text"
              inputMode="decimal"
              pattern="[0-9]+(\.[0-9]+)?"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">订单 ID（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.orderId ?? ''}
              onChange={(e) =>
                setForm({ ...form, orderId: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">合同 ID（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.contractId ?? ''}
              onChange={(e) =>
                setForm({ ...form, contractId: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">税号（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.taxId ?? ''}
              onChange={(e) =>
                setForm({ ...form, taxId: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">附件 ID（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              placeholder="附件引用，不要粘贴 OSS URL"
              value={form.attachmentId ?? ''}
              onChange={(e) =>
                setForm({ ...form, attachmentId: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">备注（可选）</label>
            <textarea
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) =>
                setForm({ ...form, notes: e.target.value || undefined })
              }
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '创建草稿'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canCreate = hasPermission(permissions, FINANCE_PERMISSIONS.INVOICE_CREATE);
  const canIssue = hasPermission(permissions, FINANCE_PERMISSIONS.INVOICE_ISSUE);
  const canVoid = hasPermission(permissions, FINANCE_PERMISSIONS.INVOICE_VOID);
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.INVOICE_READ);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, fetchList } = useInvoices();
  const { submit, issue, reject, voidInvoice, loading: actionLoading } =
    useInvoiceActions();

  const refresh = () =>
    fetchList({ status: statusFilter || undefined, search: search || undefined });

  useEffect(() => {
    if (canRead) {
      void refresh();
    }
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看发票的权限。</div>
    );
  }

  async function onSubmit(row: InvoiceSummaryDto) {
    const ok = await submit(row.id, { expectedVersion: row.version });
    if (ok) void refresh();
  }
  async function onIssue(row: InvoiceSummaryDto) {
    const ok = await issue(row.id, { expectedVersion: row.version });
    if (ok) void refresh();
  }
  async function onReject(row: InvoiceSummaryDto) {
    const reason = window.prompt('请输入驳回原因：');
    if (!reason) return;
    const ok = await reject(row.id, { expectedVersion: row.version, reason });
    if (ok) void refresh();
  }
  async function onVoid(row: InvoiceSummaryDto) {
    const voidReason = window.prompt('请输入作废原因：');
    if (!voidReason) return;
    const ok = await voidInvoice(row.id, {
      expectedVersion: row.version,
      voidReason,
    });
    if (ok) void refresh();
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">发票管理</h1>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            新建发票草稿
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="发票号或抬头"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">发票号</th>
              <th className="px-3 py-2 text-left">客户</th>
              <th className="px-3 py-2 text-left">抬头</th>
              <th className="px-3 py-2 text-left">类型</th>
              <th className="px-3 py-2 text-left">方式</th>
              <th className="px-3 py-2 text-right">金额</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">更新时间</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">{row.invoiceNo}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.customerId}</td>
                <td className="px-3 py-2">{row.title}</td>
                <td className="px-3 py-2">
                  {INVOICE_TYPE_LABELS[row.invoiceType] ?? row.invoiceType}
                </td>
                <td className="px-3 py-2">
                  {INVOICE_METHOD_LABELS[row.invoiceMethod] ?? row.invoiceMethod}
                </td>
                <td className="px-3 py-2 text-right">{fmtCny(row.amount)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {new Date(row.updatedAt).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {row.status === 'draft' && canCreate && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onSubmit(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                      >
                        提交
                      </button>
                    )}
                    {row.status === 'submitted' && canIssue && (
                      <>
                        <button
                          disabled={actionLoading}
                          onClick={() => void onIssue(row)}
                          className="rounded border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700"
                        >
                          开票
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => void onReject(row)}
                          className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700"
                        >
                          驳回
                        </button>
                      </>
                    )}
                    {row.status === 'rejected' && canCreate && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onSubmit(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                      >
                        重新提交
                      </button>
                    )}
                    {row.status !== 'voided' && canVoid && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onVoid(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600"
                      >
                        作废
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  暂无发票
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateInvoiceDialog
        key={createOpen ? 'open' : 'closed'}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refresh()}
      />
    </div>
  );
}
